import React from "react";
import { Card, Table } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { Link, useRevalidator, useRouteLoaderData } from "react-router-dom";

import { useAppApi } from "@/App";
import {
    ASTRUM_DEVELOPER_IDS,
    DeveloperFinanceModeration,
    GamePublicationState,
    GameType,
    useChoiceLabel,
} from "@/const";
import { useRequest } from "@components/AppHook";
import {
    GamePublicationStateDisplay,
    GamePublicationWishlistStateDisplay,
} from "@components/Displays/GamePublicationStateDisplay";
import { DevForm } from "@components/Forms/Fields";
import { Awesome } from "@components/Helpers";
import { CommonCard } from "@components/Pages";
import { url_reverse } from "@components/Utils";

export default function System({ gameId, header }) {
    const { t } = useTranslation();
    const { apiData, setApiData, apiUrl } = useAppApi(url_reverse("api:game:system", { game_id: gameId }));
    const revalidator = useRevalidator();
    const commonInfo = useRouteLoaderData("commonInfo");
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const { api_post } = useRequest();
    const GameGcCheckBanTrans = useChoiceLabel("GameGcCheckBan");
    const GameGcPersListUrlKindTrans = useChoiceLabel("GameGcPersListUrlKind");
    const GameGcAcceptLoginTrans = useChoiceLabel("GameGcAcceptLogin");
    const GameGcServerTypeTrans = useChoiceLabel("GameGcServerType");

    if (!apiData) return;

    const headerTools = gameCommonInfo.game.apk_filename && (
        <Link to={url_reverse("games:apk_upload", { game_id: gameId })} className="btn btn-sm btn-primary">
            <Awesome type="cloud-upload" /> {t("Загрузка APK")}
        </Link>
    );

    const refreshApiSecret = (ev) => {
        api_post(ev, apiUrl, {
            params: { action: "refresh_api_secret" },
            then: (resp) => {
                if (resp.status === "ok") revalidator.revalidate();
            },
        });
    };

    const changeAuthForwarding = () => {
        api_post(null, apiUrl, {
            params: { action: "change_auth_forwarding" },
            then: (resp) => {
                if (resp.status === "ok") {
                    setApiData((prev) => ({ ...prev, auth_forwarding: resp.auth_forwarding }));
                }
            },
        });
    };

    const mailerSubscribe = (ev) => {
        api_post(ev, apiUrl, {
            params: { action: "mailer_subscribe", is_active: ev.target.checked ? 1 : 0 },
            then: (resp) => {
                if (resp.status === "ok") {
                    const newApiData = { ...apiData };
                    newApiData.subscribe.is_active = resp.is_active;
                    setApiData(newApiData);
                }
            },
        });
    };

    const is_astrum_game_developer = ASTRUM_DEVELOPER_IDS.includes(gameCommonInfo.developer.id);

    const getAstrumField = () => {
        return (
            <>
                <tr>
                    <th>{t("Сервер Игрового Центра")}</th>
                    <td>{GameGcServerTypeTrans[gameCommonInfo.game.gc_server_type]}</td>
                </tr>
                <tr>
                    <th>{t("Проверка бана")}</th>
                    <td>{GameGcCheckBanTrans[gameCommonInfo.game.gc_check_ban]}</td>
                </tr>
                <tr>
                    <th>{t("Ключ API проверки бана")}</th>
                    <td>{gameCommonInfo.game.gc_check_ban_secret}</td>
                </tr>
                <tr>
                    <th>{t("URL вызова проверки бана")}</th>
                    <td style={{ wordBreak: "break-all" }}>{gameCommonInfo.game.gc_check_ban_url}</td>
                </tr>
                <tr>
                    <th>{t("Тип URL получения персонажей")}</th>
                    <td>{GameGcPersListUrlKindTrans[gameCommonInfo.game.gc_pers_list_url_kind]}</td>
                </tr>
                <tr>
                    <th>{t("URL вызова получения списка персонажей")}</th>
                    <td style={{ wordBreak: "break-all" }}>{gameCommonInfo.game.gc_pers_list_url}</td>
                </tr>
                <tr>
                    <th>{t("Поддержка авторизации")}</th>
                    <td>{GameGcAcceptLoginTrans[gameCommonInfo.game.gc_accept_login]}</td>
                </tr>
                <tr>
                    <th>{t("Разрешена ли авторегистрация")}</th>
                    <td>{gameCommonInfo.game.gc_autoreg ? t("Да") : t("Нет")}</td>
                </tr>
                <tr>
                    <th>{t("Ссылка на страницу регистрации для ИЦ")}</th>
                    <td style={{ wordBreak: "break-all" }}>{gameCommonInfo.game.gc_registration_webform_url}</td>
                </tr>
                <tr>
                    <th>{t("Ссылка на скрипт, управляющей входом в игру")}</th>
                    <td style={{ wordBreak: "break-all" }}>{gameCommonInfo.game.gc_shards_url}</td>
                </tr>
                <tr>
                    <th>{t("Урл страницы проекта My.com (GamePageUrl)")}</th>
                    <td style={{ wordBreak: "break-all" }}>{gameCommonInfo.game.gc_game_page_url}</td>
                </tr>
                <tr>
                    <th>{t("Местоположение профиля игры на ПК")}</th>
                    <td>{gameCommonInfo.game.gc_user_profile_path}</td>
                </tr>
                <tr>
                    <th>{t("Ссылка на опрос после удаления игры")}</th>
                    <td style={{ wordBreak: "break-all" }}>{gameCommonInfo.game.gc_after_delete_url}</td>
                </tr>
                <tr>
                    <th>MRAC url</th>
                    <td style={{ wordBreak: "break-all" }}>{gameCommonInfo.game.gc_mrac_url}</td>
                </tr>
            </>
        );
    };
    return (
        <>
            <CommonCard header={header} headerTools={headerTools}>
                {gameCommonInfo.game && (
                    <>
                        <Table bordered>
                            <tbody>
                                <tr>
                                    <th style={{ width: "40%" }}>{t("Состояние")}</th>
                                    <td>
                                        <GamePublicationStateDisplay state={gameCommonInfo.game.publication_state} />
                                        <GamePublicationWishlistStateDisplay
                                            state={gameCommonInfo.game.publication_wishlist_state}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th>{t("ID Игры (GMRID)")}</th>
                                    <td>{gameCommonInfo.game.id}</td>
                                </tr>
                                <tr>
                                    <th>{t("ID в Игровом центре (GCID)")}</th>
                                    <td>{gameCommonInfo.game.gc_id}</td>
                                </tr>
                                {(commonInfo.account.is_super ||
                                    is_astrum_game_developer ||
                                    ![GameType.DOWNLOAD, GameType.PARTNER].includes(gameCommonInfo.game.type)) && (
                                    <>
                                        <tr>
                                            <th>{t("ID в My tracker")}</th>
                                            <td>{gameCommonInfo.game.my_tracker_app_id}</td>
                                        </tr>
                                        <tr>
                                            <th>{t("ID в SOUZ")}</th>
                                            <td>{gameCommonInfo.game.project_id}</td>
                                        </tr>
                                    </>
                                )}
                                {(commonInfo.account.is_super ||
                                    is_astrum_game_developer ||
                                    ![GameType.DOWNLOAD, GameType.PARTNER].includes(gameCommonInfo.game.type)) && (
                                    <tr>
                                        <th>{t("ID в SEZAM (client_id)")}</th>
                                        <td>{gameCommonInfo.game.sezam_client_id}</td>
                                    </tr>
                                )}
                                {(commonInfo.account.is_super || is_astrum_game_developer) &&
                                    gameCommonInfo.game.sezam_project_id && (
                                        <tr>
                                            <th>sid</th>
                                            <td>{gameCommonInfo.game.sezam_project_id}</td>
                                        </tr>
                                    )}
                                <tr>
                                    <th>{t("Секрет для API игр")}</th>
                                    <td className="dev-inline-control">
                                        <DevForm.TextInput value={gameCommonInfo.game.api_secret} readOnly={true} />
                                        {gameCommonInfo.game.api_secret && (
                                            <>
                                                <a
                                                    className="btn btn-light"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(gameCommonInfo.game.api_secret);
                                                    }}
                                                >
                                                    <Awesome type="clipboard" />
                                                </a>{" "}
                                            </>
                                        )}
                                        <DevForm.Button
                                            variant="light"
                                            name="api_secret_refresh"
                                            onClick={refreshApiSecret}
                                        >
                                            <Awesome type="refresh" />
                                        </DevForm.Button>
                                    </td>
                                </tr>
                                {gameCommonInfo.game.type === GameType.DOWNLOAD && (
                                    <tr>
                                        <th>{t("Проброс авторизации")}</th>
                                        <td>
                                            <DevForm.Checkbox
                                                id="auth_forwarding"
                                                type="switch"
                                                checked={apiData.auth_forwarding}
                                                disabled={apiData.auth_forwarding === null}
                                                onChange={changeAuthForwarding}
                                            />
                                        </td>
                                    </tr>
                                )}
                                {is_astrum_game_developer && getAstrumField()}
                            </tbody>
                        </Table>

                        {gameCommonInfo.game.publication_state === GamePublicationState.CONFIGURATION && (
                            <SwitchMerchantSetButton />
                        )}
                    </>
                )}
            </CommonCard>

            {apiData.subscribe && !apiData.subscribe.need_mailer_project && (
                <Card className="mt-4">
                    <Card.Header>{t("Подписка")}</Card.Header>
                    <Card.Body>
                        <p>
                            <Trans>
                                Подробнее про оформление блока "Подписки" вы можете прочитать в{" "}
                                <a href="https://documentation.vkplay.ru/f2p_vkp/f2p_subscribtions_vkp" target="_blank">
                                    документации
                                </a>
                            </Trans>
                        </p>

                        <DevForm.Checkbox
                            id={"mailer_subscribe"}
                            label={t("Активировать")}
                            checked={apiData.subscribe.is_active}
                            onChange={mailerSubscribe}
                        />
                    </Card.Body>
                </Card>
            )}
        </>
    );
}

function SwitchMerchantSetButton() {
    const { t } = useTranslation();
    const { api_post } = useRequest();
    const revalidator = useRevalidator();
    const { game, is_test_merchant_set } = useRouteLoaderData("gameCommonInfo");
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");

    const apiUrl = url_reverse("api:game:system", { game_id: game.id });

    const switchMerchantSet = (ev) => {
        api_post(ev, apiUrl, {
            params: { action: "switch_merchant_set" },
            then: (resp) => {
                if (resp.status === "ok") {
                    revalidator.revalidate();
                }
            },
        });
    };

    return is_test_merchant_set ? (
        <>
            <div className="dmg-callout dmg-callout-info mb-3">
                {t(
                    'Платежная система работает в режиме тестирования. Для тестов используйте карту "4111 1111 1111 1111, 12/24, 123, 3DS пароль 12345678". Внимание: поддерживаются только платежи в рублях.'
                )}
            </div>
            <DevForm.Button
                variant="primary"
                onClick={switchMerchantSet}
                name="switch_merch_set"
                disabled={gameCommonInfo.developer.finance_moderation !== DeveloperFinanceModeration.APPROVED}
            >
                {t("Включить боевую платежку")}
            </DevForm.Button>
        </>
    ) : (
        <>
            <div className="dmg-callout dmg-callout-info mb-3">
                {t("Платежная система работает в нормальном режиме")}
            </div>
            <DevForm.Button variant="primary" onClick={switchMerchantSet} name="switch_merch_set">
                {t("Включить тестовую платежку")}
            </DevForm.Button>
        </>
    );
}
